import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicBySlug, getPopularComics } from '@/mocks/comics';
import { useJsonLd, generateBreadcrumbSchema, generateArticleSchema } from '@/utils/seo';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = getComicBySlug(slug || '');

  const relatedComics = getPopularComics(4).filter((c) => c.slug !== slug);

  const comicUrl = comic ? `https://jxjdxwx.cn/comic/${comic.slug}` : '';
  const breadcrumbSchemaItems = comic
    ? [
        { name: '首页', url: 'https://jxjdxwx.cn/' },
        { name: `${comic.category}漫画`, url: `https://jxjdxwx.cn/category/${comic.categorySlug}` },
        { name: comic.title, url: comicUrl },
      ]
    : [{ name: '首页', url: 'https://jxjdxwx.cn/' }];

  useJsonLd(generateBreadcrumbSchema(breadcrumbSchemaItems));
  useJsonLd(
    comic
      ? generateArticleSchema({
          title: comic.title,
          author: comic.author,
          description: comic.description,
          coverImage: comic.coverImage,
          updatedAt: comic.updatedAt,
          url: comicUrl,
        })
      : { '@context': 'https://schema.org', '@type': 'WebPage' }
  );

  useEffect(() => {
    if (comic) {
      document.title = `${comic.title} - 丫丫漫画免费在线阅读`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', comic.description.slice(0, 150));
      }
    }
  }, [comic]);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-24 pb-20 px-4 md:px-6 min-h-[60vh] flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-200 mx-auto mb-4">
              <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您查找的漫画不存在或已下架</p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const formattedViews = comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views.toString();

  const breadcrumbItems = [
    { name: '首页', url: '/' },
    { name: `${comic.category}漫画`, url: `/category/${comic.categorySlug}` },
    { name: comic.title },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="pt-24 pb-6 px-4 md:px-6 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <section className="px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-64 shrink-0">
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} 漫画封面`}
                    title={`${comic.title} - 丫丫漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">{comic.title}</h1>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <span className="text-sm text-foreground-500">作者：{comic.author}</span>
                  <span className="text-foreground-300">|</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium text-white whitespace-nowrap ${
                    comic.status === '连载中' ? 'bg-primary-500' : 'bg-foreground-600'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-500">{comic.category}</span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-500">{formattedViews}次阅读</span>
                </div>
                <div className="flex items-center gap-1 mt-3">
                  {Array.from({ length: 5 }, (_, i) => (
                    <i
                      key={i}
                      className={`text-lg ${i < Math.floor(comic.rating) ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                    ></i>
                  ))}
                  <span className="ml-1 text-sm font-semibold text-accent-600">{comic.rating}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
                <p className="mt-5 text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                <div className="flex items-center gap-4 mt-6">
                  <Link
                    to={`/comic/${comic.slug}`}
                    className="px-8 py-2.5 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    开始阅读
                  </Link>
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="px-6 py-2.5 border border-background-300 text-foreground-600 rounded-full text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    更多{comic.category}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl font-heading text-foreground-900 mb-6">章节列表</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {comic.chapters.map((chapter) => (
                <Link
                  key={chapter.id}
                  to={`/comic/${comic.slug}`}
                  className="flex items-center justify-between p-3.5 rounded-lg bg-background-50 hover:bg-primary-50 transition-colors duration-200 cursor-pointer"
                >
                  <span className="text-sm text-foreground-700 font-medium truncate">{chapter.title}</span>
                  <span className="text-xs text-foreground-400 ml-2 shrink-0 whitespace-nowrap">{chapter.updatedAt}</span>
                </Link>
              ))}
            </div>
            {comic.chapters.length > 15 && (
              <div className="text-center mt-8">
                <button className="px-8 py-2.5 border border-primary-300 text-primary-600 rounded-full text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap">
                  展开全部 {comic.chapters.length} 话
                </button>
              </div>
            )}
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="px-4 md:px-6 py-14 bg-background-50">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl font-heading text-foreground-900 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}